// resources/js/Pages/Formula/WYSIWYGCanvas.js
// DND: FINAL FIX - Logic is now driven by the insertion line's parent context.

import React, { useState, Fragment, useEffect, useRef, useMemo } from 'react';
import { Card, Table, Nav, Button, Form } from 'react-bootstrap';
import { useDndContext, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from './SortableItem';
import InsertionPoint from './components/InsertionPoint';
import InlineEditor from './InlineEditor';
import { v4 as uuid } from 'uuid';

import {
  addFieldAtPosition,
  addListAtPosition,
  addTextBlockAtPosition,
  addSectionAtPosition,
  addDividerAtPosition,
  addGraphAtPosition,
  deleteNode,
  updateNodeKey,
  updateNodeInstruction,
  updateNodeDisplay,
  updateTextBlockContent,
  updateSectionTitle,
  updateNodeRenderAs,
  updateNodeChartType,
  updateNodeValueType,   // ✅ NEW
  updateNodeCollapsible, // ✅ NEW
  updateNodeShowTitle,   // ✅ NEW: Import the new function
  updateNodeStacked,     // ✅ ADD THIS
  updateNodeCardWidth,   // ✅ ADD THIS
  updateNodeLayoutColumns, // ✅ ADD THIS
  convertFieldToArray,
  createElementAtPosition, // ✅ NEW
  updateNodeType,          // ✅ NEW
  updateNodeProperty       // ✅ NEW
} from './utils/schemaUpdates';

// ✅ ELEMENT TYPE TEMPLATES - For intelligent type changing
const FIELD_TYPE_TEMPLATES = {
  string: {
    key: 'Text Field',
    valueType: 'string',
    instruction: 'Provide a detailed description or value.',
  },
  score: {
    key: 'Score Field',
    valueType: 'score',
    instruction: 'Provide a score from 1 to 10.',
  },
  importance: {
    key: 'Importance Badge',
    valueType: 'importance',
    instruction: 'Specify the level of importance (low, medium, or high).',
  },
  scene_number: {
    key: 'Scene Link',
    valueType: 'scene_number',
    instruction: 'Provide a scene number.',
  },
  scene_list: {
    key: 'Scene List',
    valueType: 'scene_list',
    instruction: 'Provide a list of scene numbers.',
  },
  questions_for_llm: {
    key: 'AI Follow-up Questions',
    type: 'array',
    display: 'ul',
    renderAs: 'llm_questions',
    children: [],
    instruction: 'Based on your analysis, provide 2-3 follow-up questions a writer might ask to improve their understanding or lead them to better changes to their script.',
  },
};

// DND: Helpers to find nodes and their parents within the schema tree.
const findNodeAndParent = (startNode, nodeId, parent = null) => {
  if (!startNode) return null;
  if (startNode.id === nodeId) {
    return { node: startNode, parent };
  }
  if (startNode.children) {
    for (const [index, child] of startNode.children.entries()) {
      const found = findNodeAndParent(child, nodeId, startNode);
      if (found) {
        found.index = index;
        return found;
      }
    }
  }
  return null;
};

function DroppableContainer({ id, children, node, parentId }) {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      type: 'container',
      node: node,
      isEmpty: !node.children || node.children.length === 0,
      parentId: parentId
    }
  });

  const { active } = useDndContext();
  const isDragging = !!active;

  // ✅ FIX: Use the 'isOver' state directly from the hook. This is much more reliable.
  const shouldHighlight = isOver;

  const isEmpty = !node.children || node.children.length === 0;

  const style = {
    border: shouldHighlight ? '3px solid #0d6efd' : '3px solid transparent',
    backgroundColor: shouldHighlight ? 'rgba(13, 110, 253, 0.1)' : 'transparent',
    padding: '1rem',
    borderRadius: '8px',
    transition: 'none',
    minHeight: isDragging ? '120px' : (isEmpty ? '120px' : '60px'),
    position: 'relative',
    boxShadow: shouldHighlight ? '0 0 10px rgba(13, 110, 253, 0.3)' : 'none',
    transform: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-droppable-container="true"
      data-container-id={id}
    >
      {children}
    </div>
  );
}

// Main component
export default function WYSIWYGCanvas({ schema, onSchemaChange, activeId, pointerCoordinates }) {
  // State management
  const [activeTabStates, setActiveTabStates] = useState({});
  const [editingCellStates, setEditingCellStates] = useState({});
  const [newlyCreatedFieldId, setNewlyCreatedFieldId] = useState(null);

  // Clear the newly created field ID after a short delay to allow auto-focus
  useEffect(() => {
    if (newlyCreatedFieldId) {
      const timer = setTimeout(() => {
        setNewlyCreatedFieldId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [newlyCreatedFieldId]);

  // Add CSS styles for improved drag and drop UX
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* ✅ FIX: Disable ALL transitions and animations to prevent jumping */
      .wysiwyg-empty-container {
        transition: none !important;
      }
      .wysiwyg-empty-container:hover {
        /* Completely disable hover effects during drag operations */
      }
      .wysiwyg-drop-target-active {
        /* Static styling only - no animations */
        background-color: rgba(13, 110, 253, 0.15) !important;
        border-color: #0056b3 !important;
      }
      
      /* Disable all keyframe animations */
      @keyframes pulseInsertionLine {
        0%, 100% { 
          opacity: 0.7;
          transform: scaleY(1);
        }
      }
      
      /* Disable transitions during drag operations to prevent jumping */
      .dragging * {
        transition: none !important;
        animation: none !important;
        transform: none !important;
      }
      
      /* ✅ FIX: Ensure dragged items don't cause layout shifts */
      [data-sortable-item][data-is-dragging="true"] {
        /* Keep the space occupied but make invisible */
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        /* Maintain exact layout dimensions */
        position: relative !important;
        transform: none !important;
      }
      
      /* Enhanced drop zone indicators - all static, no animations */
      [data-droppable-container] {
        position: relative;
        transition: none !important;
      }
      
      [data-droppable-container].drop-target-parent {
        background-color: rgba(13, 110, 253, 0.03);
        transition: none !important;
      }
      
      [data-droppable-container].drop-target-parent::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border: 2px solid #0d6efd;
        border-radius: 10px;
        pointer-events: none;
        opacity: 0.7;
        transition: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const setActiveTab = (nodeId, tabIndex) => {
    setActiveTabStates(prev => ({ ...prev, [nodeId]: tabIndex }));
  };

  const getActiveTab = (nodeId) => {
    return activeTabStates[nodeId] || 0;
  };

  const setEditingCell = (nodeId, cellId) => {
    setEditingCellStates(prev => ({ ...prev, [nodeId]: cellId }));
  };

  const getEditingCell = (nodeId) => {
    return editingCellStates[nodeId] || null;
  };

  // All handlers for schema updates
  const allHandlers = {
    onAddFieldAtPosition: (parentId, position) => {
      const result = addFieldAtPosition(schema, parentId, position);
      setNewlyCreatedFieldId(result.newFieldId);
      onSchemaChange(result.schema);
    },
    onAddListAtPosition: (parentId, position) => {
      onSchemaChange(addListAtPosition(schema, parentId, position));
    },
    onAddTextBlockAtPosition: (parentId, position) => {
      onSchemaChange(addTextBlockAtPosition(schema, parentId, position));
    },
    onAddSectionAtPosition: (parentId, position) => {
      onSchemaChange(addSectionAtPosition(schema, parentId, position));
    },
    onAddDividerAtPosition: (parentId, position) => {
      onSchemaChange(addDividerAtPosition(schema, parentId, position));
    },
    // ✅ NEW HANDLER
    onAddGraphAtPosition: (parentId, position) => {
      onSchemaChange(addGraphAtPosition(schema, parentId, position));
    },
    // ✅ NEW: Generic element creation handler
    onCreateElementAtPosition: (parentId, position, elementTemplate) => {
      const result = createElementAtPosition(schema, parentId, position, elementTemplate);
      if (elementTemplate.type === 'field') {
        setNewlyCreatedFieldId(result.newElementId);
      }
      onSchemaChange(result.schema);
    },
    onDeleteNode: (nodeId) => {
      onSchemaChange(deleteNode(schema, nodeId));
    },
    onUpdateNodeKey: (nodeId, newKey) => {
      onSchemaChange(updateNodeKey(schema, nodeId, newKey));
    },
    onUpdateNodeInstruction: (nodeId, newInstruction) => {
      onSchemaChange(updateNodeInstruction(schema, nodeId, newInstruction));
    },
    onUpdateNodeDisplay: (nodeId, newDisplay) => {
      onSchemaChange(updateNodeDisplay(schema, nodeId, newDisplay));
    },
    onUpdateTextBlockContent: (nodeId, newContent) => {
      onSchemaChange(updateTextBlockContent(schema, nodeId, newContent));
    },
    onUpdateSectionTitle: (nodeId, newTitle) => {
      onSchemaChange(updateSectionTitle(schema, nodeId, newTitle));
    },
    // ✅ NEW HANDLER
    onUpdateNodeRenderAs: (nodeId, newRenderAs) => {
      onSchemaChange(updateNodeRenderAs(schema, nodeId, newRenderAs));
    },
    // ✅ NEW HANDLER
    onUpdateNodeChartType: (nodeId, newChartType) => {
      onSchemaChange(updateNodeChartType(schema, nodeId, newChartType));
    },
    // ✅ NEW HANDLER
    onUpdateNodeValueType: (nodeId, newValueType) => {
      onSchemaChange(updateNodeValueType(schema, nodeId, newValueType));
    },
    // ✅ NEW HANDLER
    onUpdateNodeCollapsible: (nodeId, isCollapsible) => {
      onSchemaChange(updateNodeCollapsible(schema, nodeId, isCollapsible));
    },
    // ✅ NEW HANDLER: Show Title toggle
    onUpdateNodeShowTitle: (nodeId, showTitle) => {
      onSchemaChange(updateNodeShowTitle(schema, nodeId, showTitle));
    },
    // ✅ ADD THIS NEW HANDLER
    onUpdateNodeStacked: (nodeId, isStacked) => {
      onSchemaChange(updateNodeStacked(schema, nodeId, isStacked));
    },
    // ✅ ADD THIS NEW HANDLER
    onUpdateNodeCardWidth: (nodeId, cardWidth) => {
      onSchemaChange(updateNodeCardWidth(schema, nodeId, cardWidth));
    },
    // ✅ ADD THIS NEW HANDLER
    onUpdateNodeLayoutColumns: (nodeId, columns) => {
      onSchemaChange(updateNodeLayoutColumns(schema, nodeId, columns));
    },
    // ✅ NEW HANDLERS FOR ROW DETAIL
    onUpdateNodeProperty: (nodeId, property, value) => {
      onSchemaChange(updateNodeProperty(schema, nodeId, property, value));
    },
    onToggleRowDetail: (nodeId, enabled) => {
      if (enabled) {
        const rowDetailTemplate = {
          type: 'row_detail',
          triggerField: '',
          triggerValues: [],
          instruction: 'Provide detail for expandable rows. Set row_detail to null for rows that should not expand.',
          children: []
        };
        const findNode = (node, id) => {
          if (node.id === id) return node;
          for (const child of (node.children || [])) {
            const found = findNode(child, id);
            if (found) return found;
          }
          return null;
        };
        const targetNode = findNode(schema, nodeId);
        const position = targetNode ? (targetNode.children || []).length : 0;
        const result = createElementAtPosition(schema, nodeId, position, rowDetailTemplate);
        onSchemaChange(result.schema);
      } else {
        const findNode = (node, id) => {
          if (node.id === id) return node;
          for (const child of (node.children || [])) {
            const found = findNode(child, id);
            if (found) return found;
          }
          return null;
        };
        const targetNode = findNode(schema, nodeId);
        if (targetNode) {
          const rowDetail = (targetNode.children || []).find(c => c.type === 'row_detail');
          if (rowDetail) {
            onSchemaChange(deleteNode(schema, rowDetail.id));
          }
        }
      }
    },
    // ✅ NEW: Node type change handler
    onUpdateNodeType: (nodeId, newTypeTemplate) => {
      onSchemaChange(updateNodeType(schema, nodeId, newTypeTemplate));
    },
    onConvertFieldToArray: (nodeId, displayType) => {
      onSchemaChange(convertFieldToArray(schema, nodeId, displayType));
    }
  };

  if (!schema || !schema.id) {
    return <div>Schema not loaded.</div>;
  }

  return (
    <div className={`wysiwyg-canvas ${activeId ? 'dragging' : ''}`}>
      <div className="wysiwyg-preview border rounded p-4 bg-white shadow-sm">
        {/* DND: Pass the full schema down for context */}
        {renderContainer(schema, allHandlers, 0, activeId, schema, { setActiveTab, getActiveTab, setEditingCell, getEditingCell }, newlyCreatedFieldId, null, pointerCoordinates)}
      </div>
    </div>
  );
}

// Static renderer for the DragOverlay
function StaticRender({ node }) {
  if (!node) return null;
  const component = (
    <div style={{ display: 'flex', alignItems: 'flex-start', position: 'relative' }}>
      <span style={{ cursor: 'grabbing', padding: '8px', color: '#aaa' }}>⋮⋮</span>
      <div style={{ flexGrow: 1 }}>
        {(() => {
          switch (node.type) {
            case 'field': return <div className="p-3 border rounded bg-light"><strong>Field:</strong> {node.key}</div>;
            case 'section': return <div className="p-3 border rounded bg-secondary text-white"><strong>Section:</strong> {node.title}</div>;
            case 'array': return <div className="p-3 border rounded bg-info-subtle"><strong>Array:</strong> {node.key}</div>;
            case 'text-block': return <div className="p-3 border rounded bg-warning-subtle"><strong>Text Block</strong></div>;
            case 'divider': return <div className="p-3 border rounded bg-light"><strong>Divider</strong></div>;
            case 'graph': return <div className="p-3 border rounded bg-success-subtle"><strong>📊 Graph:</strong> {node.key}</div>;
            default: return <div>{node.key}</div>;
          }
        })()}
      </div>
    </div>
  );
  return component;
}
WYSIWYGCanvas.StaticRender = StaticRender;

// Renders a container (Root, Section, Array) and its children
function renderContainer(node, handlers, depth = 0, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId = null, pointerCoordinates = null) {
  if (!node || node.children === undefined) return null;

  const children = node.children || [];
  const childIds = useMemo(() => children.map(child => child.id), [children]);
  const isSimpleList = (node.type === 'list' || node.type === 'array') && node.display === 'ul' && children.length === 0 && node.key !== 'Root';

  // ✅ FIX: Create a special component for empty containers to properly use hooks
  if (children.length === 0 && !isSimpleList) {
    return <EmptyContainer node={node} parentId={parentId} handlers={handlers} />;
  }

  // --- This is the original logic for NON-EMPTY containers, which now works correctly ---
  const containerContent = isSimpleList ? (
    <div className="text-center p-4 text-muted" style={{ minHeight: '60px', border: '2px dashed #28a745', borderRadius: '6px', backgroundColor: '#f8fff8' }}>
      📝 Simple List (contains text values)
    </div>
  ) : (
    <>
      {children.map((child, index) => (
        <SortableItem key={child.id} id={child.id} activeId={activeId} node={child}>
          {(dragListeners, isOver, overNodeRect, draggingRect) => (
            <div key={child.id}>
              <InsertionPoint position={index} parentId={node.id} onAddElement={handlers.onCreateElementAtPosition} />
              <div className="mb-1">
                {renderNode(child, handlers, depth, dragListeners, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, node.id, pointerCoordinates)}
              </div>
            </div>
          )}
        </SortableItem>
      ))}
      <InsertionPoint position={children.length} parentId={node.id} onAddElement={handlers.onCreateElementAtPosition} isLast={true} />
    </>
  );

  return (
    <DroppableContainer id={node.id} node={node} parentId={parentId}>
      <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
        {containerContent}
      </SortableContext>
    </DroppableContainer>
  );
}

// ✅ NEW: Component for empty containers that properly uses hooks
function EmptyContainer({ node, parentId, handlers }) { // <-- Step 2a: Accept 'handlers' prop
  const { setNodeRef, isOver } = useDroppable({
    id: node.id,
    data: {
      type: 'container',
      node: node,
      isEmpty: true,
      parentId: parentId
    }
  });

  // Step 2b: Create a handler function for the new button
  const handleAddFirstItem = () => {
    // Determine if we're in a table to provide a better label
    const isTable = node.display === 'table';
    const fieldTemplate = {
      type: 'field',
      key: isTable ? 'new_column' : 'new_field',
      instruction: 'Provide a detailed description or value.',
      valueType: 'string',
    };
    // Call the handler to add the new field at the first position (index 0)
    handlers.onCreateElementAtPosition?.(node.id, 0, fieldTemplate);
  };

  // Step 2c: Update the JSX to include the button and dynamic text
  return (
    <div
      ref={setNodeRef}
      className={`text-center p-4 text-muted wysiwyg-empty-container ${isOver ? 'wysiwyg-drop-target-active' : ''}`}
      style={{
        minHeight: '140px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: isOver ? '3px solid #0d6efd' : '3px dashed #dee2e6',
        borderRadius: '8px',
        backgroundColor: isOver ? 'rgba(13, 110, 253, 0.1)' : '#f8f9fa',
        transition: 'none',
        boxShadow: isOver ? '0 0 10px rgba(13, 110, 253, 0.3)' : 'none',
        cursor: 'default',
        margin: '4px',
      }}
    >
      <div className="mb-2" style={{ fontSize: '1.5rem' }}>{isOver ? '🎯' : '📥'}</div>
      <div style={{ fontWeight: isOver ? 'bold' : 'normal' }}>
        {isOver ? 'Release to drop here!' : `Drop items here, or...`}
      </div>

      <Button
        variant="primary"
        size="sm"
        className="mt-3"
        onClick={handleAddFirstItem}
      >
        <i className="fas fa-plus me-2"></i>
        {node.display === 'table' ? 'Add First Column' : 'Add First Field'}
      </Button>
    </div>
  );
}

// Renders an individual node (Field, Section, etc.)
function renderNode(node, handlers, depth = 0, dragListeners, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId, pointerCoordinates) {
  if (!node) return null;
  const type = node.type || 'field';

  const componentWithHandle = (component) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', position: 'relative' }}>
      <span {...dragListeners} style={{ cursor: 'grab', padding: '8px', color: '#aaa' }} title="Drag to reorder">⋮⋮</span>
      <div style={{ flexGrow: 1 }}>{component}</div>
    </div>
  );

  switch (type) {
    case 'field':
      return componentWithHandle(renderField(node, handlers, depth, newlyCreatedFieldId));
    case 'array':
      return componentWithHandle(renderArray(node, handlers, depth, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId, pointerCoordinates));
    case 'section':
      return componentWithHandle(renderSection(node, handlers, depth, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId, pointerCoordinates));
    case 'text-block':
      return componentWithHandle(renderTextBlock(node, handlers, depth));
    case 'divider':
      return componentWithHandle(renderDivider(node, handlers, depth));
    // ✅ NEW CASE
    case 'graph':
      return componentWithHandle(renderGraph(node, handlers, depth));
    case 'row_detail':
      return componentWithHandle(renderRowDetail(node, handlers, depth, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId, pointerCoordinates));
    default:
      return <div>Unknown type: {type}</div>;
  }
}

// Render a basic field
// ✅ REPLACED: Render a field with in-node controls for type and collapsibility
function renderField(node, handlers, depth, newlyCreatedFieldId) {
  const isNewField = newlyCreatedFieldId === node.id;
  const keyEditorRef = useRef(null);
  const instructionEditorRef = useRef(null);
  const valueType = node.valueType || 'string';

  const fieldTypes = [
    { value: 'string', label: '📝 Text', description: 'Standard text input' },
    { value: 'score', label: '💯 Score (1-10)', description: 'Numeric score with color coding' },
    { value: 'importance', label: '⚠️ Importance Badge', description: 'Low/Medium/High badge' },
    { value: 'scene_number', label: '🎬 Scene Link', description: 'Link to scene number' },
    { value: 'scene_list', label: '📋 Scene List', description: 'List of scene numbers' },
    { value: 'questions_for_llm', label: '🤖 AI Follow-up Questions', description: 'Interactive AI questions' },
  ];

  return (
    <Card className="mb-2 border-primary">
      <Card.Body className="p-2 position-relative">
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center mb-2">
              <strong className="me-2">Field:</strong>
              <InlineEditor
                ref={keyEditorRef}
                value={node.key}
                onChange={(newKey) => handlers.onUpdateNodeKey(node.id, newKey)}
                placeholder="field_name"
                className="fw-bold me-3"
                autoFocus={isNewField}
              />
            </div>
            <InlineEditor
              ref={instructionEditorRef}
              value={node.instruction}
              onChange={(instruction) => handlers.onUpdateNodeInstruction(node.id, instruction)}
              placeholder="Describe what this field should contain..."
              className="form-control"
            />
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handlers.onDeleteNode(node.id)}
            title="Delete field"
            className="ms-2 align-self-start"
          >
            ×
          </Button>
        </div>

        <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top flex-wrap gap-2">
          <Form.Group className="d-flex align-items-center me-3">
            <Form.Label className="small text-muted me-2 mb-0">Field Type:</Form.Label>
            <Form.Select
              size="sm"
              value={valueType}
              onChange={(e) => {
                const newValueType = e.target.value;
                const template = FIELD_TYPE_TEMPLATES[newValueType];
                if (template && newValueType !== valueType) {
                  // Use the intelligent type changing function
                  handlers.onUpdateNodeType(node.id, template);
                } else {
                  // Fallback to simple valueType update
                  handlers.onUpdateNodeValueType(node.id, newValueType);
                }
              }}
              style={{ maxWidth: '220px' }}
              title="Choose how this field should be displayed and behave"
            >
              {fieldTypes.map(ft => (
                <option key={ft.value} value={ft.value} title={ft.description}>
                  {ft.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="d-flex align-items-center me-3">
            <Form.Label className="small text-muted me-2 mb-0">Style:</Form.Label>
            <Form.Select
              size="sm"
              value={node.style || ''}
              onChange={(e) => handlers.onUpdateNodeProperty(node.id, 'style', e.target.value)}
              style={{ maxWidth: '140px' }}
              title="Visual style preset for this field in the output"
            >
              <option value="">Default</option>
              <option value="warning">⚠️ Warning</option>
              <option value="danger">🚨 Danger</option>
              <option value="info">ℹ️ Info</option>
              <option value="success">✅ Success</option>
              <option value="action">🎯 Action</option>
              <option value="neutral">📌 Neutral</option>
            </Form.Select>
          </Form.Group>

          <Form.Check
            type="switch"
            id={`bold-wysiwyg-${node.id}`}
            label={<span className="small text-muted">Bold</span>}
            checked={!!node.bold}
            onChange={(e) => handlers.onUpdateNodeProperty(node.id, 'bold', e.target.checked)}
            className="me-3"
          />

          <Form.Check
            type="switch"
            id={`collapsible-wysiwyg-${node.id}`}
            label={<span className="small text-muted">Collapsible</span>}
            checked={!!node.collapsible}
            onChange={(e) => handlers.onUpdateNodeCollapsible(node.id, e.target.checked)}
          />
        </div>
      </Card.Body>
    </Card>
  );
}

// Render a section with its children
function renderSection(node, handlers, depth, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId, pointerCoordinates) {
  return (
    <Card className="mb-2 border-secondary">
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <strong className="me-2">Section:</strong>
            <InlineEditor
              value={node.title}
              onChange={(newTitle) => handlers.onUpdateSectionTitle(node.id, newTitle)}
              placeholder="Section Title"
              className="fw-bold"
            />
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handlers.onDeleteNode(node.id)}
            title="Delete section"
          >
            ×
          </Button>
        </div>
        {/* DND: The nested call to renderContainer creates the isolated context */}
        {renderContainer(node, handlers, depth + 1, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, node.id, pointerCoordinates)}

        {/* ✅ ADD THIS NEW FOOTER */}
        <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
          <Form.Group className="d-flex align-items-center">
            <Form.Label className="small text-muted me-2 mb-0">Layout:</Form.Label>
            <Form.Select
              size="sm"
              value={node.layoutColumns || 1}
              onChange={(e) => handlers.onUpdateNodeLayoutColumns(node.id, e.target.value)}
              style={{ maxWidth: '120px' }}
              title="Set the column layout for items inside this section."
            >
              <option value="1">1 Column</option>
              <option value="2">2 Columns</option>
              <option value="3">3 Columns</option>
            </Form.Select>
          </Form.Group>
        </div>
      </Card.Body>
    </Card>
  );
}

// Render an array with its display options
// ✅ CORRECTED: Render an array with proper distinction for AI Questions
function renderArray(node, handlers, depth, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId, pointerCoordinates) {

  // ✅ THE CORE FIX: This check now correctly distinguishes the special type.
  const isLlmQuestionList = node.renderAs === 'llm_questions';

  return (
    <Card className={`mb-2 ${isLlmQuestionList ? 'border-warning' : 'border-info'}`}>
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            {/* Use a clear, distinct title for the special component */}
            <strong className="me-2">{isLlmQuestionList ? '🤖 AI Questions:' : 'Array:'}</strong>
            <InlineEditor
              value={node.key}
              onChange={(newKey) => handlers.onUpdateNodeKey(node.id, newKey)}
              placeholder="array_name"
              className="fw-bold"
            />
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handlers.onDeleteNode(node.id)}
            title="Delete element"
          >
            ×
          </Button>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="fw-medium small">Instruction</Form.Label>
          <InlineEditor
            value={node.instruction}
            onChange={(newInstruction) => handlers.onUpdateNodeInstruction(node.id, newInstruction)}
            placeholder="Describe what this element should do or contain..."
            multiline={true}
            className="form-control"
          />
        </Form.Group>

        {/* ✅ CONDITIONAL RENDERING: This is the corrected logic */}
        {isLlmQuestionList ? (
          // If it's the special type, show a non-editable placeholder.
          <div className="text-center p-3 text-muted bg-light rounded">
            <div style={{ fontSize: '1.5rem' }}>🤖</div>
            <div className="fw-bold">AI-Generated Question List</div>
            <small>This element is a special list that will be displayed to the end-user as interactive buttons. You cannot add fields to it.</small>
          </div>
        ) : (
          // For ALL OTHER arrays, show the normal container for adding fields.
          <>
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium small">Display Type</Form.Label>
              <Form.Select
                size="sm"
                value={node.display || 'table'}
                onChange={(e) => handlers.onUpdateNodeDisplay(node.id, e.target.value)}
              >
                <option value="table">Table</option>
                <option value="cards">Cards</option>
                <option value="tabs">Tabs</option>
                <option value="ul">Simple List (UL)</option>
              </Form.Select>
            </Form.Group>
            {renderContainer(node, handlers, depth + 1, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, node.id, pointerCoordinates)}
          </>
        )}

        <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
          {/* Show Title toggle - only for UL lists */}
          {(node.display === 'ul' && !isLlmQuestionList) && (
            <Form.Check
              type="switch"
              id={`show-title-wysiwyg-${node.id}`}
              label={<span className="small text-muted">Show Title</span>}
              checked={!!node.showTitle}
              onChange={(e) => handlers.onUpdateNodeShowTitle(node.id, e.target.checked)}
              className="me-3"
            />
          )}

          {/* For cards display, show all controls in one row */}
          {node.display === 'cards' ? (
            <>
              {/* Collapsible toggle for cards */}
              <Form.Check
                type="switch"
                id={`collapsible-wysiwyg-${node.id}`}
                label={<span className="small text-muted">Collapsible</span>}
                checked={!!node.collapsible}
                onChange={(e) => handlers.onUpdateNodeCollapsible(node.id, e.target.checked)}
                className="me-3"
              />

              {/* Card width buttons */}
              <div className="d-flex align-items-center me-3">
                <span className="small text-muted me-2">Width:</span>
                <div className="btn-group btn-group-sm">
                  <Button
                    variant={!node.cardWidth || node.cardWidth === 'auto' ? 'primary' : 'outline-secondary'}
                    onClick={() => handlers.onUpdateNodeCardWidth(node.id, 'auto')}
                    title="Automatic width, up to 3 cards per row on large screens."
                    size="sm"
                  >
                    Auto
                  </Button>
                  <Button
                    variant={node.cardWidth === 'half' ? 'primary' : 'outline-secondary'}
                    onClick={() => handlers.onUpdateNodeCardWidth(node.id, 'half')}
                    title="Half width, 2 cards per row on large screens."
                    size="sm"
                  >
                    Half
                  </Button>
                  <Button
                    variant={node.cardWidth === 'full' ? 'primary' : 'outline-secondary'}
                    onClick={() => handlers.onUpdateNodeCardWidth(node.id, 'full')}
                    title="Full width, 1 card per row. Ideal for wide content."
                    size="sm"
                  >
                    Full
                  </Button>
                </div>
              </div>

              {/* Layout columns dropdown for cards */}
              {!isLlmQuestionList && node.children && node.children.length > 0 && (
                <Form.Group className="d-flex align-items-center">
                  <Form.Label className="small text-muted me-2 mb-0">Columns:</Form.Label>
                  <Form.Select
                    size="sm"
                    value={node.layoutColumns || 1}
                    onChange={(e) => handlers.onUpdateNodeLayoutColumns(node.id, e.target.value)}
                    style={{ maxWidth: '80px' }}
                    title="Set the column layout for items inside this container."
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                  </Form.Select>
                </Form.Group>
              )}
            </>
          ) : (
            <>
              {/* Default collapsible toggle for non-cards */}
              <Form.Check
                type="switch"
                id={`collapsible-wysiwyg-${node.id}`}
                label={<span className="small text-muted">Collapsible</span>}
                checked={!!node.collapsible}
                onChange={(e) => handlers.onUpdateNodeCollapsible(node.id, e.target.checked)}
              />

              {/* Stacked layout + Row Detail toggles for tables */}
              {node.display === 'table' && (
                <>
                  <Form.Check
                    type="switch"
                    id={`stacked-wysiwyg-${node.id}`}
                    label={<span className="small text-muted">Stacked Layout</span>}
                    checked={!!node.isStacked}
                    onChange={(e) => handlers.onUpdateNodeStacked(node.id, e.target.checked)}
                    className="ms-3"
                    title="Forces table rows to stack vertically. Ideal for tables in narrow containers like cards."
                  />
                  <Form.Check
                    type="switch"
                    id={`row-detail-wysiwyg-${node.id}`}
                    label={<span className="small text-muted">Row Detail</span>}
                    checked={!!(node.children || []).find(c => c.type === 'row_detail')}
                    onChange={(e) => handlers.onToggleRowDetail(node.id, e.target.checked)}
                    className="ms-3"
                    title="Add expandable detail content that shows when a row is clicked."
                  />
                </>
              )}

              {/* Layout columns dropdown for non-cards containers */}
              {!isLlmQuestionList && node.children && node.children.length > 0 && (
                <Form.Group className="d-flex align-items-center ms-3">
                  <Form.Label className="small text-muted me-2 mb-0">Columns:</Form.Label>
                  <Form.Select
                    size="sm"
                    value={node.layoutColumns || 1}
                    onChange={(e) => handlers.onUpdateNodeLayoutColumns(node.id, e.target.value)}
                    style={{ maxWidth: '80px' }}
                    title="Set the column layout for items inside this container."
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                  </Form.Select>
                </Form.Group>
              )}
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// Render a text block
function renderTextBlock(node, handlers, depth) {
  return (
    <Card className="mb-2 border-warning">
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>Text Block</strong>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handlers.onDeleteNode(node.id)}
            title="Delete text block"
          >
            ×
          </Button>
        </div>
        <InlineEditor
          value={node.content}
          onChange={(newContent) => handlers.onUpdateTextBlockContent(node.id, newContent)}
          placeholder="Add your text here..."
          multiline={true}
          className="form-control"
        />
      </Card.Body>
    </Card>
  );
}

// Render a divider
function renderDivider(node, handlers, depth) {
  return (
    <Card className="mb-1 border-light">
      <Card.Body className="p-1 d-flex justify-content-between align-items-center bg-light">
        <div className="flex-grow-1 text-center">
          <hr className="my-1" />
          <small className="text-muted">Divider</small>
        </div>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handlers.onDeleteNode(node.id)}
          title="Delete divider"
        >
          ×
        </Button>
      </Card.Body>
    </Card>
  );
}

function renderRowDetail(node, handlers, depth, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, parentId, pointerCoordinates) {
  return (
    <Card className="mb-2 border-warning" style={{ backgroundColor: '#fff9e6' }}>
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <strong className="me-2">↕️ Row Detail</strong>
            <span className="text-muted small">Expands below table rows when clicked</span>
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handlers.onDeleteNode(node.id)}
            title="Remove row detail"
          >
            ×
          </Button>
        </div>

        {/* Trigger config */}
        <div className="row mb-3">
          <div className="col-md-4">
            <Form.Group>
              <Form.Label className="fw-medium small">Trigger Field</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={node.triggerField || ''}
                onChange={(e) => handlers.onUpdateNodeProperty(node.id, 'triggerField', e.target.value)}
                placeholder="e.g. gap_type"
              />
              <Form.Text className="text-muted">
                Leave empty = all rows expandable
              </Form.Text>
            </Form.Group>
          </div>
          <div className="col-md-8">
            <Form.Group>
              <Form.Label className="fw-medium small">Trigger Values (comma-separated)</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                value={(node.triggerValues || []).join(', ')}
                onChange={(e) => {
                  const values = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
                  handlers.onUpdateNodeProperty(node.id, 'triggerValues', values);
                }}
                placeholder="e.g. None, Minor, Major"
              />
            </Form.Group>
          </div>
        </div>

        {/* Instruction */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-medium small">Instruction</Form.Label>
          <InlineEditor
            value={node.instruction}
            onChange={(newInstruction) => handlers.onUpdateNodeInstruction(node.id, newInstruction)}
            placeholder="Describe what each row's detail content should contain..."
            multiline={true}
            className="form-control"
          />
        </Form.Group>

        {/* Children container — uses same renderContainer as all other containers */}
        <div className="ps-2 border-start border-warning border-3">
          {renderContainer(node, handlers, depth + 1, activeId, fullSchema, stateHelpers, newlyCreatedFieldId, node.id, pointerCoordinates)}
        </div>
      </Card.Body>
    </Card>
  );
}

// ✅ REPLACED: Render a graph placeholder with a mode switcher
// Helper component for renderGraph to use hooks
function GraphComponent({ node, handlers, depth }) {
  // Use local state to ensure dropdown updates immediately
  const [localRenderAs, setLocalRenderAs] = React.useState(node.renderAs || 'diagram');

  // Sync local state when node changes
  React.useEffect(() => {
    setLocalRenderAs(node.renderAs || 'diagram');
  }, [node.renderAs]);

  const chartType = node.chartType || 'line';

  const info = {
    diagram: {
      icon: '📈',
      title: 'Diagram',
      borderColor: 'border-primary',
    },
    chart: {
      icon: '📊',
      title: 'Data Chart',
      borderColor: 'border-success',
    }
  };

  const currentInfo = info[localRenderAs];

  return (
    <Card className={`mb-2 ${currentInfo.borderColor}`}>
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <strong className="me-2">{currentInfo.icon} {currentInfo.title}:</strong>
            <InlineEditor
              value={node.key}
              onChange={(newKey) => handlers.onUpdateNodeKey(node.id, newKey)}
              placeholder="graph_name"
              className="fw-bold"
            />
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handlers.onDeleteNode(node.id)}
            title={`Delete ${currentInfo.title}`}
          >
            ×
          </Button>
        </div>

        <InlineEditor
          value={node.instruction}
          onChange={(newInstruction) => handlers.onUpdateNodeInstruction(node.id, newInstruction)}
          placeholder="Describe what this graph should contain..."
          className="mb-2 d-block"
        />

        <div className="d-flex justify-content-end align-items-center gap-3">
          <Form.Group>
            <Form.Label className="small text-muted me-2 mb-0">Display As:</Form.Label>
            <Form.Select
              size="sm"
              value={localRenderAs}
              onChange={e => {
                const newRenderAs = e.target.value;
                console.log(`Changing renderAs from "${localRenderAs}" to "${newRenderAs}"`);

                // Update local state immediately for UI responsiveness
                setLocalRenderAs(newRenderAs);

                // Update backend state
                handlers.onUpdateNodeRenderAs(node.id, newRenderAs);
              }}
            >
              <option value="diagram">Diagram</option>
              <option value="chart">Chart</option>
            </Form.Select>
          </Form.Group>

          {localRenderAs === 'chart' && (
            <Form.Group>
              <Form.Label className="small text-muted me-2 mb-0">Chart Style:</Form.Label>
              <Form.Select
                size="sm"
                value={chartType}
                onChange={e => handlers.onUpdateNodeChartType(node.id, e.target.value)}
              >
                <option value="line">Line</option>
                <option value="bar">Bar</option>
                <option value="radar">Radar</option>
                <option value="pie">Pie</option>
                <option value="doughnut">Doughnut</option>
              </Form.Select>
            </Form.Group>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function renderGraph(node, handlers, depth) {
  return <GraphComponent node={node} handlers={handlers} depth={depth} />;
}
